import cn from 'classnames';
import Button from 'components/button-new';
import ImpressionTrackerNew from 'components/impression-tracker/new-impression-tracker';
import { EmptyCollectionState } from 'components/pet-profile/empty-collection-state';
import ProductCardSkeleton from 'components/product-card/skeleton';
import ProductImpressionsTracker from 'components/product-impressions-tracker';
import { useConfig } from 'config/use-config';
import { Entry } from 'contentful';
import { PetType } from 'constants/pet-type';
import { useSegmentationContext } from 'contexts/segmentation';
import { IProductPagesFields, ISectionAdsTile } from 'global';
import useMobile from 'hooks/common/use-mobile';
import useBestsellerStyleProperties from 'hooks/product/use-bestseller-style-properties';
import useTest8753 from 'hooks/test/use-test-8753';
import { CollectionProduct } from 'models/collection/collection';
import { useCollectionState } from 'modules/collection/state';
import { getProductPosition } from 'modules/collection/utils/get-product-position/get-product-position';
import dynamic from 'next/dynamic';
import { createRef, FC, ReactNode, RefObject, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRootStore } from 'stores/use-root-store';
import { Gtm } from 'tracking';
import { useAiWeightIsVisible } from 'hooks/common/use-ai-weight-is-visible';
import { useAiWeightAnalyzerStore } from 'stores/ai-weight-analyzer';
import { AI_WEIGHT_ENTRY_POINTS } from 'modules/ai-weight-analyzer/constants';
import { AiWeightTile } from 'modules/ai-weight-analyzer/components/ai-weight-tile';
import { isLegacyProductPageModel } from 'utils/contentful';
import AdTileWrapper from '../ad-tile-wrapper';
import AllergiesFilter from '../allergies-filter';
import FilterLaptop from '../collection-filter/laptop';
import FilterMobile from '../collection-filter/mobile';
import HeroFilterContainer from '../hero-filter';
import BestSellerWrapper from './best-seller-wrapper';

const ProductCard = dynamic(() => import('components/product-card'));

const CatGeneralFoodPageFilters = dynamic(
  () => import('components/pet-profile/cat-general-food-pages-filters')
);

interface IProductsGridProps {
  bestsellerSectionClassName?: string;
  containerRef?: RefObject<HTMLDivElement>;
  /** Contentful data connected with collection. if omitted, no adtiles would be shown */
  contentfulData?: Entry<IProductPagesFields> | null;
  customButton?: ReactNode;
  petProfileSelectorClassName?: string;
  productsLimit?: number;
  shouldShowAllergyFilter?: boolean;
  shouldShowBlackRibbon?: boolean;
  showFilter: boolean;
  showMoreButton?: boolean;
  trackingElementName?: string;
  /** If true, best seller ribbon is not shown */
  omitBestSeller?: boolean;
  /** Url query to be added  */
  additionalQuery?: Record<string, string>;
}

const TRACKING_NAME = 'Button Load More Products';

/**
 * This component is responsible for rendering the products grid of a collection.
 * It also renders the filter and the allergy filter.
 */
const ProductsGrid: FC<IProductsGridProps> = ({
  bestsellerSectionClassName,
  containerRef,
  contentfulData,
  customButton,
  omitBestSeller = false,
  productsLimit,
  shouldShowAllergyFilter,
  shouldShowBlackRibbon,
  showFilter,
  showMoreButton,
  trackingElementName,
  additionalQuery,
}) => {
  const { featureFlags } = useConfig();
  const featureFlg = featureFlags.dynamicUxTest;
  const petType = useRootStore((store) => store.petType);
  const { multipleSegmentation, shouldOmitPersonalization } = useSegmentationContext();
  const isMobile = useMobile();
  const isAiWeightVisible = useAiWeightIsVisible();
  const petName = useRootStore((store) => store.currentPetProfile?.name);
  const markEngaged = useAiWeightAnalyzerStore((state) => state.markEngaged);
  const openOverlay = useAiWeightAnalyzerStore((state) => state.open);

  const handleAiWeightCta = (): void => {
    Gtm.track.selectElement({
      element: ['ai-weight-analyzer', AI_WEIGHT_ENTRY_POINTS.cdpTile, petType],
    });
    markEngaged();
    openOverlay({ entryPointId: AI_WEIGHT_ENTRY_POINTS.cdpTile, petType });
  };

  const {
    isValidating,
    collection,
    limitOfProductsToShow,
    visibleProducts,
    setLimitOfProductsToShow,
    appliedFilter,
    filterConfig,
    activeAllergyFilter,
    resetFilter,
    toggleFilter,
    toggleActiveAllergyFilter,
    toggleAllergyFilter,
    trackingListName,
  } = useCollectionState();

  useEffect(() => {
    if (productsLimit) {
      setLimitOfProductsToShow(productsLimit);
    } else {
      setLimitOfProductsToShow(undefined);
    }
  }, [productsLimit, setLimitOfProductsToShow]);

  const ref = containerRef ?? createRef<HTMLDivElement>();

  const adsTiles = useMemo(() => {
    const adtiles: Array<{ index: number; value: ISectionAdsTile }> = [];
    if (contentfulData && isLegacyProductPageModel(contentfulData)) {
      contentfulData.fields.collectionsAdsTiles?.map((tile) => {
        adtiles.push({
          // subtract as position begins with 1
          index: tile.fields.position - 1,
          value: tile,
        });
      });
    }

    return adtiles;
  }, [contentfulData]);

  const visibleElements = useMemo(() => {
    const sortedAdtiles = [...adsTiles].sort((a, b) => a.index - b.index);
    const filteredBestsellers = visibleProducts.filter((product) => {
      return omitBestSeller || shouldOmitPersonalization
        ? product
        : !product.getIsBestSeller({
            segment: multipleSegmentation?.lastSegmentation,
          });
    });

    // Only when dynamic-ux is not available, we re-sort by availability
    if (!featureFlg) {
      filteredBestsellers.sort((a, b) => {
        if (a.availableForSale === b.availableForSale) return 0;
        return a.availableForSale ? -1 : 1; //
      });
    }

    const _visibleProducts: Array<ISectionAdsTile | CollectionProduct> = [
      ...filteredBestsellers,
      // limit by limitOfProductsToShow. if limitOfProductsToShow is undefined, slice returns all the elements
    ].slice(0, limitOfProductsToShow);

    for (let i = adsTiles.length - 1; i >= 0; i--) {
      const { index, value } = sortedAdtiles[i];
      if (index < _visibleProducts.length) {
        _visibleProducts.splice(index, 0, value);
      }
    }

    return _visibleProducts;
  }, [
    omitBestSeller,
    adsTiles,
    featureFlg,
    limitOfProductsToShow,
    multipleSegmentation?.lastSegmentation,
    shouldOmitPersonalization,
    visibleProducts,
  ]);

  const bestsellerProducts = useMemo(() => {
    if (omitBestSeller) return [];
    return visibleProducts.filter((product) => {
      return (
        product.availableForSale &&
        product.getIsBestSeller({
          segment: multipleSegmentation?.lastSegmentation,
        })
      );
    });
  }, [multipleSegmentation?.lastSegmentation, omitBestSeller, visibleProducts]);

  const { paddingValue, shouldApplyPadding } = useBestsellerStyleProperties({
    bestsellerProductsLength: bestsellerProducts.length,
  });

  const moreProductsWithoutVisibleProducts = useMemo(() => {
    return collection.products
      .filter(
        (product) => !visibleProducts.find((visibleProduct) => visibleProduct.id === product.id)
      )
      .slice(0, 9);
  }, [collection, visibleProducts]);

  const { hideAllFilters, hideSidebarFilters } = useTest8753();
  const showEmptyState = visibleProducts.length === 0;
  return (
    <>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-5" ref={ref}>
        {collection.products.length > 0 && showFilter && !hideAllFilters && !hideSidebarFilters ? (
          <>
            {/* laptop */}
            <div className="col-span-1 hidden lg:block">
              <FilterLaptop
                appliedFilter={appliedFilter}
                filterConfig={filterConfig}
                activeAllergyFilter={activeAllergyFilter}
                resetFilter={resetFilter}
                toggleFilter={toggleFilter}
                collectionProducts={collection.products}
                visibleProducts={visibleProducts}
              />
            </div>
            {/* mobile */}
            <div className="lg:hidden">
              <FilterMobile
                appliedFilter={appliedFilter}
                filterConfig={filterConfig}
                activeAllergyFilter={activeAllergyFilter}
                resetFilter={resetFilter}
                toggleFilter={toggleFilter}
                collectionProducts={collection.products}
                visibleProducts={visibleProducts}
              />
              {shouldShowAllergyFilter && (
                <AllergiesFilter
                  activeAllergyFilter={activeAllergyFilter}
                  collectionProducts={collection.products}
                  toggleActiveAllergyFilter={toggleActiveAllergyFilter}
                  toggleAllergyFilter={toggleAllergyFilter}
                  toggleFilter={toggleFilter}
                />
              )}
            </div>
          </>
        ) : null}
        <div
          className={cn(
            'col-span-1',
            hideAllFilters || hideSidebarFilters ? 'lg:col-span-4' : 'lg:col-span-3'
          )}
          data-test="cdp-collection-tile-wrapper"
        >
          {petType === PetType.Dog && !hideAllFilters && (
            <HeroFilterContainer
              category={filterConfig?.['hero-filter'] ? 'hero-filter' : 'feed-type'}
              className="mb-3"
              collectionProducts={collection.products}
              filterConfig={filterConfig}
            />
          )}
          {shouldShowAllergyFilter && !hideAllFilters && (
            <div className="mb-[30px] hidden lg:block">
              <AllergiesFilter
                activeAllergyFilter={activeAllergyFilter}
                collectionProducts={collection.products}
                toggleActiveAllergyFilter={toggleActiveAllergyFilter}
                toggleAllergyFilter={toggleAllergyFilter}
                toggleFilter={toggleFilter}
              />
            </div>
          )}

          {petType === PetType.Cat && !hideAllFilters && (
            <CatGeneralFoodPageFilters className="mb-4" collectionProducts={collection.products} />
          )}

          <div
            className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-5"
            data-test="cdp-collection-tile-wrapper"
          >
            {isValidating && (
              <>
                {new Array(9).fill(null).map((_, index) => (
                  <ProductCardSkeleton key={index} />
                ))}
              </>
            )}
            {!isValidating && showEmptyState && <EmptyCollectionState />}
            {!isValidating && !shouldOmitPersonalization && bestsellerProducts.length > 0 && (
              <BestSellerWrapper
                bestSellerLength={bestsellerProducts.length}
                message={collection.getBestSellerTitle({
                  segmentation: multipleSegmentation?.lastSegmentation,
                })}
                className={cn(bestsellerSectionClassName, 'lg:ml-0')}
                isMobile={isMobile}
              >
                {bestsellerProducts.map((product, index: number) => (
                  <ProductImpressionsTracker
                    element="Product List"
                    key={`${product.id}-${product.promoteVariantId}`}
                    list={collection.title}
                    position={index}
                    product={product}
                    trackingGroupName={trackingListName || ''}
                    collectionHandle={trackingListName}
                  >
                    <div
                      data-test="product-tile-container"
                      className="bg-other-white flex rounded-lg"
                    >
                      <ProductCard
                        collectionTitle={collection.title}
                        isBestSeller={true}
                        position={index}
                        product={product}
                        starSize={isMobile ? 'small' : 'medium'}
                        trackingElementName={trackingElementName}
                        additionalQuery={additionalQuery}
                        shouldShowBlackRibbon={shouldShowBlackRibbon}
                      />
                    </div>
                  </ProductImpressionsTracker>
                ))}
              </BestSellerWrapper>
            )}
            {!isValidating &&
              !showEmptyState &&
              visibleElements.map((element, index: number) => {
                const showAiWeightTile = index === 3;
                if ('handle' in element) {
                  const _shouldApplyPadding = shouldApplyPadding(index);

                  return (
                    <>
                      {showAiWeightTile && isAiWeightVisible && (
                        <AiWeightTile
                          entryPointId={AI_WEIGHT_ENTRY_POINTS.cdpTile}
                          petType={petType}
                          petName={petName}
                          onCta={handleAiWeightCta}
                          dataTest="ai-weight-entry-cdp-tile"
                        />
                      )}
                      <ProductImpressionsTracker
                        element={trackingElementName ?? 'Product List'}
                        key={`${element.id}-${element.promoteVariantId}`}
                        list={collection.title}
                        position={getProductPosition({
                          index,
                          bestsellersLength: bestsellerProducts.length,
                        })}
                        product={element}
                        trackingGroupName={trackingListName || ''}
                        collectionHandle={trackingListName}
                      >
                        <div
                          data-test="product-tile-container"
                          className="flex flex-wrap"
                          style={{
                            paddingTop: _shouldApplyPadding ? `${paddingValue}px` : '',
                            paddingBottom: _shouldApplyPadding ? '8px' : '',
                          }}
                        >
                          <ProductCard
                            collectionTitle={collection.title}
                            isBestSeller={
                              shouldOmitPersonalization
                                ? false
                                : !omitBestSeller &&
                                  element.getIsBestSeller({
                                    segment: multipleSegmentation?.lastSegmentation,
                                  })
                            }
                            position={getProductPosition({
                              index,
                              bestsellersLength: bestsellerProducts.length,
                            })}
                            product={element}
                            starSize={isMobile ? 'small' : 'medium'}
                            trackingElementName={trackingElementName}
                            additionalQuery={additionalQuery}
                            shouldShowBlackRibbon={shouldShowBlackRibbon}
                          />
                        </div>
                      </ProductImpressionsTracker>
                    </>
                  );
                } else if ('sys' in element) {
                  return (
                    <>
                      {showAiWeightTile && isAiWeightVisible && (
                        <AiWeightTile
                          entryPointId={AI_WEIGHT_ENTRY_POINTS.cdpTile}
                          petType={petType}
                          petName={petName}
                          onCta={handleAiWeightCta}
                          dataTest="ai-weight-entry-cdp-tile"
                        />
                      )}
                      <AdTileWrapper
                        entry={element}
                        key={element.sys.id}
                        title={collection.title}
                      />
                    </>
                  );
                }
              })}
          </div>
          {!limitOfProductsToShow &&
            visibleProducts.length < 9 &&
            !!moreProductsWithoutVisibleProducts.length && (
              <div className="w-full">
                <div className="border-grey-200 m-2 mt-4 border-t pt-6 text-center text-xl font-black lg:mt-10 lg:pt-8">
                  <FormattedMessage id="cdp:similarproducts" />
                </div>
                <div
                  className="grid grid-cols-1 gap-3 lg:grid-cols-3 lg:gap-5"
                  data-test="collection-list"
                >
                  {moreProductsWithoutVisibleProducts.map((product, index: number) => (
                    <ProductImpressionsTracker
                      element="Product List"
                      key={`more-${product.id}-${product.promoteVariantId}`}
                      list={collection.title}
                      position={getProductPosition({
                        index,
                        bestsellersLength: bestsellerProducts.length,
                        previousLength: visibleProducts.length,
                      })}
                      product={product}
                      trackingGroupName={trackingListName || ''}
                      collectionHandle={trackingListName}
                    >
                      <div data-test="sometext3" className={'flex'}>
                        <ProductCard
                          product={product}
                          collectionTitle={collection.title}
                          position={getProductPosition({
                            index,
                            bestsellersLength: bestsellerProducts.length,
                            previousLength: visibleProducts.length,
                          })}
                          additionalQuery={additionalQuery}
                        />
                      </div>
                    </ProductImpressionsTracker>
                  ))}
                </div>
              </div>
            )}
          {showMoreButton && limitOfProductsToShow && !customButton && (
            <ImpressionTrackerNew element={TRACKING_NAME}>
              <div className={cn('my-4 flex justify-center', isMobile && 'container')}>
                <Button
                  size="base"
                  className="w-1/2 lg:w-1/3"
                  layout="outline"
                  onClick={() => {
                    setLimitOfProductsToShow(undefined);
                    Gtm.track.selectElement({ element: TRACKING_NAME });
                  }}
                >
                  <FormattedMessage id="cdp:standalone-cdp:load-more" />
                </Button>
              </div>
            </ImpressionTrackerNew>
          )}
          {customButton}
        </div>
      </div>
    </>
  );
};

export default ProductsGrid;
